import { Fragment, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import produce from 'immer';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { isArray, isEqual, isPlainObject, uniqBy } from 'lodash';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  AddOutlined,
  Close,
  DeleteOutlined,
  OfflineBolt,
  ArrowDownwardRounded as ArrowDownIcon,
  MergeRounded as MergeIcon,
} from '@mui/icons-material';
import {
  editorUtils,
  getLayoutComponentLabel,
  ILayoutComponent,
  LayoutComponentNames,
  Loader,
  SelectableOption,
  useEntities,
  useEditorStore,
  IFlowConfig,
  IFlowStepConfig,
  configureQuery,
  isEmptyId,
} from '@diana/core';
import { useUpdateActivity } from '../../Support/useUpdateActivity';
import { useAddActivity } from '../../Support/useAddActivity';
import { TFlowBuilder2PageModalMap } from '../../Store/FlowBuilder2PageStore.types';
import { FLOW_BUILDER2_PAGE_NS } from '../../i18n';

type TAssetSearchOption = {
  component: ILayoutComponent;
  stepConfig: IFlowStepConfig | null;
};

type TValueMappingItem = {
  from: TAssetSearchOption | null;
  to: TAssetSearchOption | null;
};

export function UpdateAssetValue(props: {
  ctx: TFlowBuilder2PageModalMap['UpdateAssetValueActivityEditor'];
  onClose: () => void;
}) {
  const { ctx, onClose } = props;

  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);
  const { palette, breakpoints } = useTheme();

  const resolveContext = useResolveContext(ctx);

  const [activityDisplayName, setActivityDisplayName] = useState('');
  const [selectedAssetSearch, setSelectedAssetSearch] = useState<TAssetSearchOption | null>(null);
  const [dataMapping, setDataMapping] = useState<TValueMappingItem[]>([{ from: null, to: null }]);

  const actionSetId = useEditorStore((s) => resolveContext(s.flow)?.step.actionSetId ?? '');
  const actionId = useEditorStore((s) => resolveContext(s.flow)?.action.id ?? '');
  const activity = useEditorStore((s) => resolveContext(s.flow)?.activity ?? null, isEqual);

  const allLayoutComponents = useEditorStore((s) => {
    if (s.flow) {
      const assetSearches = s.flow.steps.reduce<TAssetSearchOption[]>((result, stepConfig) => {
        const layout = s.getLayoutById(stepConfig.layoutId);
        if (layout) {
          for (const item of layout.components) {
            result.push({ stepConfig, component: item });
          }
        }
        return result;
      }, []);

      return uniqBy(assetSearches, (as) => as.component.id);
    }

    return [];
  }, isEqual);

  const previousAndCurrentLayoutComponents = useEditorStore((s) => {
    if (s.flow) {
      const currentStepConfigIndex = s.flow.steps.findIndex(
        (step) => step.taskId === ctx.taskId && step.actionSetId === ctx.actionSetId,
      );
      const prevAndCurrentStepConfigs = s.flow.steps.slice(0, currentStepConfigIndex + 1);
      const assetSearches = prevAndCurrentStepConfigs.reduce<TAssetSearchOption[]>(
        (result, stepConfig) => {
          const layout = s.getLayoutById(stepConfig.layoutId);
          if (layout) {
            for (const item of layout.components) {
              result.push({ stepConfig, component: item });
            }
          }
          return result;
        },
        [],
      );

      return uniqBy(assetSearches, (as) => as.component.id);
    }

    return [];
  }, isEqual);

  const { data: entities, isLoading: isLoadingEntities } = useEntities();

  const selectedAssetType = useMemo(() => {
    const entityType = selectedAssetSearch?.component.properties.thingType;
    return entities?.find((e) => e.entityType === entityType) ?? null;
  }, [entities, selectedAssetSearch]);

  const {
    flow: selectedAssetTypeFlow,
    layout: selectedAssetTypeLayout,
    isLoading: isLoadingAssetTypeLayout,
  } = useAssetTypeLayout(selectedAssetType?.flow?.id ?? '');

  const isDataMappingValid = useMemo(() => {
    return dataMapping.every(({ from, to }) => !!from && !!to);
  }, [dataMapping]);

  const getDestinationOptions = useCallback(
    (from: TAssetSearchOption) => {
      const takenIds = dataMapping.map((v) => v.to?.component.id ?? '').filter(Boolean);
      const components = selectedAssetTypeLayout?.components ?? [];

      return components
        .filter((item) => {
          // not the same component type
          if (from.component.component !== item.component) {
            return false;
          }

          // already been selected
          if (takenIds.includes(item.id)) {
            return false;
          }

          if (from.component.component === LayoutComponentNames.INPUT_TEXT) {
            if (from.component.properties.inputType !== item.properties.inputType) {
              return false;
            }
          }

          return true;
        })
        .map<TAssetSearchOption>((component) => ({ component, stepConfig: null }));
    },
    [dataMapping, selectedAssetTypeLayout?.components],
  );

  const { updateActivity, isUpdatingActivity } = useUpdateActivity({
    onSuccess: () => {
      onClose();
    },
  });

  const { addActivity, isAddingActivity } = useAddActivity({
    onSuccess: () => {
      onClose();
    },
  });

  const onApply = useCallback(() => {
    if (!selectedAssetSearch || !selectedAssetTypeFlow) return;
    if (activity) {
      updateActivity({
        actionSetId,
        actionId,
        activityId: activity.id,
        data: {
          ...activity,
          displayName: activityDisplayName,
          properties: {
            ...activity.properties,
            entityType: selectedAssetSearch.component.properties.thingType,
            entityIdDataPath: selectedAssetSearch.component.dataPath + '.id',
            entityTypeComponentId: selectedAssetSearch.component.id,
            publishOperation: {
              ...activity.properties.publishOperation,
              sectionName: selectedAssetTypeFlow.steps[0].taskName,
              dataMapping: dataMapping
                .filter((e) => Boolean(e.from) && Boolean(e.to))
                .map((e) => ({
                  fromPath: e.from!.component.dataPath,
                  fromPathComponentId: e.from!.component.id,
                  toProperty: e.to!.component.dataPath,
                  toPropertyComponentId: e.to!.component.id,
                })),
            },
          },
        },
      }).catch();
    } else {
      addActivity({
        actionSetId,
        actionId,
        data: editorUtils.createActivityConfigObject({
          name: 'UpdateAssetValueActivity',
          displayName: activityDisplayName,
          properties: {
            enable: true,
            fromDatePath: '',
            entityType: selectedAssetSearch.component.properties.thingType,
            entityIdDataPath: selectedAssetSearch.component.dataPath + '.id',
            entityTypeComponentId: selectedAssetSearch.component.id,
            dataPath: '$',
            toSectionName: '',
            publishOperation: {
              sectionName: selectedAssetTypeFlow.steps[0].taskName,
              dataMapping: dataMapping
                .filter((e) => Boolean(e.from) && Boolean(e.to))
                .map((e) => ({
                  fromPath: e.from!.component.dataPath,
                  fromPathComponentId: e.from!.component.id,
                  toProperty: e.to!.component.dataPath,
                  toPropertyComponentId: e.to!.component.id,
                })),
              excludeProperties: [],
              dataType: 'None',
              updateType: 'Merge',
              targetPath: '',
              identifier: '',
              fromSection: '',
              rootDataPath: '',
              lookup: null,
            },
          },
        }),
      }).catch();
    }
  }, [
    actionId,
    actionSetId,
    activity,
    activityDisplayName,
    addActivity,
    dataMapping,
    selectedAssetSearch,
    selectedAssetTypeFlow,
    updateActivity,
  ]);

  useEffect(() => {
    setActivityDisplayName(activity?.displayName ?? t('titles.updateAssetValue'));
  }, [t, activity?.displayName]);

  useEffect(() => {
    const entityTypeComponentId = activity?.properties?.entityTypeComponentId;
    const found = allLayoutComponents.find((item) => item.component.id === entityTypeComponentId);
    setSelectedAssetSearch(found ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity?.properties?.entityTypeComponentId]);

  useEffect(() => {
    const publishOperation = activity?.properties?.publishOperation;
    if (isPlainObject(publishOperation) && isArray(publishOperation.dataMapping)) {
      const mapping = (publishOperation.dataMapping as any[]).reduce<TValueMappingItem[]>(
        (result, item) => {
          const mappingItem: TValueMappingItem = { from: null, to: null };
          if (isPlainObject(item)) {
            const sourceId = item.fromPathComponentId;
            const targetId = item.toPropertyComponentId;
            const targetLC = selectedAssetTypeLayout?.components.find((v) => v.id === targetId);
            mappingItem.from = allLayoutComponents.find((v) => v.component.id === sourceId) ?? null;
            mappingItem.to = targetLC ? { component: targetLC, stepConfig: null } : null;
          }
          return [...result, mappingItem];
        },
        [],
      );
      setDataMapping(mapping.length ? mapping : [{ from: null, to: null }]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity?.properties?.publishOperation, selectedAssetTypeLayout?.components]);

  return (
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        borderRadius: '4px',
        transform: 'translate(-50%, -50%)',
        width: 600,
        [breakpoints.down('md')]: { width: '90%' },
        backgroundColor: palette.background.paper,
      }}
    >
      <Stack py={4} px={2} spacing={2}>
        {/* Display Name Editor */}
        <Stack direction="row" alignItems="center">
          <OfflineBolt />
          <TextField
            fullWidth
            variant="standard"
            placeholder={t('labels.updateAssetValue')}
            InputProps={{ disableUnderline: true }}
            value={activityDisplayName}
            onChange={(e) => setActivityDisplayName(e.target.value)}
          />
          <Box flex={1} />
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>

        {/* Value Mapping */}
        <Stack direction="row" spacing={2}>
          <Box width={40}>
            <Stack
              direction="column"
              justifyContent="flex-start"
              alignItems="center"
              sx={{ height: '130%' }}
            >
              <IconButton
                disableRipple
                sx={{
                  cursor: 'default',
                  borderRadius: 2,
                  backgroundColor: palette.background.card,
                }}
              >
                <ArrowDownIcon
                  sx={{
                    color: ({ palette }) => palette.primary.main,
                  }}
                />
              </IconButton>
              <Box
                sx={{
                  height: '100%',
                  border: '0.5px solid',
                  borderColor: palette.text.secondary,
                  borderStyle: 'dashed',
                }}
              />
            </Stack>
          </Box>
          <Box flex={1}>
            <Typography>{t('labels.where')}:</Typography>
            <Typography sx={{ mt: 1, color: palette.text.secondary }}>
              {t('labels.assetToUpdate')}:
            </Typography>
            <Autocomplete
              options={previousAndCurrentLayoutComponents.filter(
                (item) =>
                  item.component.properties.thingType &&
                  item.component.component === LayoutComponentNames.SEARCH_THINGS,
              )}
              value={selectedAssetSearch}
              onChange={(_, asset) => setSelectedAssetSearch(asset ? asset : null)}
              size="small"
              multiple={false}
              isOptionEqualToValue={(option, value) => option.component.id === value.component.id}
              getOptionLabel={(option) => getLayoutComponentLabel(option.component)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('labels.selectAssetSearchField')}
                  InputProps={{
                    sx: {
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '1px',
                      },
                    },
                    ...params.InputProps,
                  }}
                />
              )}
              renderOption={(props, option) => {
                const secondary = [
                  option.stepConfig?.stepDisplayName,
                  option.stepConfig?.taskDisplayName,
                ].filter(Boolean);
                return (
                  <SelectableOption
                    key={option.component.id}
                    listItemProps={props}
                    primary={getLayoutComponentLabel(option.component)}
                    secondary={secondary.join(' / ') || undefined}
                  />
                );
              }}
            />
          </Box>
          <Box width={40} />
        </Stack>

        <Stack direction="row" spacing={2}>
          <Box width={40}>
            <Stack direction="column" justifyContent="flex-start" alignItems="center">
              <IconButton
                disableRipple
                sx={{
                  cursor: 'default',
                  borderRadius: 2,
                  backgroundColor: ({ palette }) => palette.background.card,
                }}
              >
                <MergeIcon
                  sx={{
                    cursor: 'default',
                    color: ({ palette }) => palette.primary.main,
                    transform: 'rotate(90deg)',
                  }}
                />
              </IconButton>
            </Stack>
          </Box>
          <Box flex={1}>
            {isLoadingAssetTypeLayout ? (
              <Loader />
            ) : (
              <Box>
                <Typography>{t('labels.then')}:</Typography>
                <Box
                  sx={{
                    mt: 1,
                    maxHeight: '600px',
                    overflowY: 'auto',
                  }}
                >
                  {dataMapping.map((each, index) => (
                    <Fragment key={index}>
                      <Stack direction="row" mb={1}>
                        <Box flex={1}>
                          <Typography sx={{ color: palette.text.secondary }}>
                            {t('labels.takeValueFrom')}:
                          </Typography>
                          <Autocomplete
                            options={previousAndCurrentLayoutComponents.filter((e) =>
                              [
                                LayoutComponentNames.INPUT_TEXT,
                                LayoutComponentNames.DATE_PICKER,
                              ].includes(e.component.component),
                            )}
                            onChange={(_, value) => {
                              setDataMapping(
                                produce(dataMapping, (draft) => {
                                  if (!value) draft[index] = { from: null, to: null };
                                  else draft[index] = { from: value, to: null };
                                }),
                              );
                            }}
                            value={each.from ?? null}
                            size="small"
                            multiple={false}
                            disabled={!selectedAssetType}
                            sx={{
                              ...(!selectedAssetType ? { pointerEvents: 'none' } : undefined),
                            }}
                            isOptionEqualToValue={(option, value) =>
                              option.component.id === value.component.id
                            }
                            getOptionLabel={(option) => getLayoutComponentLabel(option.component)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={t('labels.selectComponent')}
                                InputProps={{
                                  sx: {
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                      borderWidth: '1px',
                                    },
                                  },
                                  ...params.InputProps,
                                }}
                              />
                            )}
                            renderOption={(props, option) => {
                              const secondary = [
                                option.stepConfig?.stepDisplayName,
                                option.stepConfig?.taskDisplayName,
                              ].filter(Boolean);
                              return (
                                <SelectableOption
                                  key={option.component.id}
                                  listItemProps={props}
                                  primary={getLayoutComponentLabel(option.component)}
                                  secondary={secondary.join(' / ') || undefined}
                                />
                              );
                            }}
                          />
                        </Box>
                        <Box width={10} />
                        <Box flex={1}>
                          {(() => {
                            const isDisabled = !selectedAssetSearch || !each.from;
                            return (
                              <Fragment>
                                <Typography sx={{ color: palette.text.secondary }}>
                                  {t('labels.updateValueAt')}:
                                </Typography>
                                <Autocomplete
                                  options={each.from ? getDestinationOptions(each.from) : []}
                                  onChange={(_, value) => {
                                    setDataMapping(
                                      produce(dataMapping, (draft) => {
                                        draft[index].to = value ?? null;
                                      }),
                                    );
                                  }}
                                  value={each.to ?? null}
                                  loading={isLoadingAssetTypeLayout}
                                  size="small"
                                  multiple={false}
                                  disabled={isDisabled}
                                  sx={{
                                    ...(isDisabled ? { pointerEvents: 'none' } : undefined),
                                  }}
                                  isOptionEqualToValue={(option, value) =>
                                    option.component.id === value.component.id
                                  }
                                  getOptionLabel={(option) =>
                                    getLayoutComponentLabel(option.component)
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder={t('labels.selectAssetProperty')}
                                      InputProps={{
                                        sx: {
                                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderWidth: '1px',
                                          },
                                        },
                                        ...params.InputProps,
                                      }}
                                    />
                                  )}
                                />
                              </Fragment>
                            );
                          })()}
                        </Box>
                        <Box
                          sx={{
                            width: '55px',
                            display: 'grid',
                            placeItems: 'flex-end',
                          }}
                        >
                          {index !== 0 ? (
                            <IconButton
                              onClick={() =>
                                setDataMapping(
                                  produce(dataMapping, (draft) => {
                                    draft.splice(index, 1);
                                  }),
                                )
                              }
                            >
                              <DeleteOutlined />
                            </IconButton>
                          ) : null}
                        </Box>
                      </Stack>
                      {dataMapping.length - 1 === index ? (
                        <Button
                          variant="text"
                          startIcon={<AddOutlined color="primary" />}
                          endIcon={null}
                          onClick={() =>
                            setDataMapping((value) => [...value, { from: null, to: null }])
                          }
                          disableElevation
                          disableRipple
                          sx={{ mt: 1 }}
                        >
                          {t('labels.value')}
                        </Button>
                      ) : null}
                    </Fragment>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Stack>

        <Stack direction="row" justifyContent="flex-end">
          <LoadingButton
            variant="contained"
            disabled={isLoadingEntities || isLoadingAssetTypeLayout || !isDataMappingValid}
            onClick={onApply}
            loading={isAddingActivity || isUpdatingActivity}
            sx={{ width: '100px' }}
          >
            {t('buttons.apply')}
          </LoadingButton>
        </Stack>
      </Stack>
    </Box>
  );
}

const useResolveContext = (ctx: TFlowBuilder2PageModalMap['UpdateAssetValueActivityEditor']) => {
  const indexRef = useRef({
    stepIndex: -1,
    actionIndex: -1,
  });

  return useCallback(
    (flow: IFlowConfig | null) => {
      if (flow) {
        const step = flow.steps[indexRef.current.stepIndex];
        if (step && step.taskId === ctx.taskId && step.actionSetId === ctx.actionSetId) {
          const action = step.actions[indexRef.current.actionIndex];
          if (action.id === ctx.actionId) {
            const activity = action.activities?.[ctx.activityIndex] ?? null;
            return { step, action, activity };
          }
        }

        for (let stepIndex = 0; stepIndex < flow.steps.length; stepIndex++) {
          const step = flow.steps[stepIndex];
          if (step && step.taskId === ctx.taskId && step.actionSetId === ctx.actionSetId) {
            for (let actionIndex = 0; actionIndex < step.actions.length; actionIndex++) {
              const action = step.actions[actionIndex];
              if (action.id === ctx.actionId) {
                const activity = action.activities?.[ctx.activityIndex] ?? null;
                indexRef.current = { stepIndex, actionIndex };
                return { step, action, activity };
              }
            }
          }
        }
      }

      return null;
    },
    [ctx.actionId, ctx.actionSetId, ctx.activityIndex, ctx.taskId],
  );
};

const useAssetTypeLayout = (flowId: string) => {
  const { key: getFlowKey, fn: getFlowFn } = configureQuery.getFlow({ flowId });
  const { data: flow, isLoading: isLoadingFlow } = useQuery({
    queryKey: getFlowKey,
    queryFn: getFlowFn,
    enabled: !!flowId,
    refetchOnMount: true,
  });

  const layoutId = useMemo(() => {
    return flow?.steps.find((step) => !isEmptyId(step.layoutId))?.layoutId ?? '';
  }, [flow?.steps]);

  const { key: getLayoutKey, fn: getLayoutFn } = configureQuery.getLayout({ layoutId });
  const { data: layout, isLoading: isLoadingLayout } = useQuery({
    queryKey: getLayoutKey,
    queryFn: getLayoutFn,
    enabled: !!layoutId,
    retryOnMount: true,
  });

  return {
    flow,
    layout,
    isLoading: isLoadingFlow || isLoadingLayout,
  };
};
